import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, storeAuthData, setupTokenRefreshTimer } from '../../utils/api';

const TeamLoginPage = () => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ username: '', password: '', organisation_id: '' });
  const [orgIdInput, setOrgIdInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mobileAuthError, setMobileAuthError] = useState('');
  const [searchParams] = useSearchParams();

  // Check for mobile auth errors in URL
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'invalid_token') {
      setMobileAuthError('Your session link is invalid. Please log in manually.');
    } else if (errorParam === 'token_expired') {
      setMobileAuthError('Your session has expired. Please log in again.');
    } else if (errorParam === 'exchange_failed') {
      setMobileAuthError('Unable to authenticate from mobile app. Please log in manually.');
    }
  }, [searchParams]);

  // Format organisation ID with automatic hyphens
  const formatOrgId = (value) => {
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const limited = cleaned.slice(0, 14);
    let formatted = '';
    for (let i = 0; i < limited.length; i++) {
      if (i === 4 || i === 6 || i === 8) {
        formatted += '-';
      }
      formatted += limited[i];
    }
    return formatted;
  };

  const handleOrgIdChange = (e) => {
    const value = e.target.value;
    const formatted = formatOrgId(value);
    setOrgIdInput(formatted);
    const fullOrgId = formatted ? `SO-ORG-${formatted}` : '';
    setLoginData({...loginData, organisation_id: fullOrgId});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/login`, {
        ...loginData,
        device_id: `web-${Date.now()}`,
        device_name: 'Web Browser',
        device_platform: 'web'
      });

      if (response.data.access_token) {
        // Store all authentication data including refresh token
        storeAuthData(response.data);
        localStorage.setItem('userType', 'user');
        localStorage.setItem('signInTime', new Date().toISOString());
        
        // Also store with team_ prefix for backward compatibility
        localStorage.setItem('team_token', response.data.access_token);
        localStorage.setItem('team_user', JSON.stringify(response.data.user || {}));
        
        // Set up proactive token refresh
        setupTokenRefreshTimer();
        
        console.log('✅ Team member login successful, tokens set:', {
          hasToken: !!localStorage.getItem('token'),
          hasRefreshToken: !!localStorage.getItem('refreshToken'),
          userType: localStorage.getItem('userType')
        });
        
        navigate('/team/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.message) {
        setError(detail.message);
      } else {
        setError(detail || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f1a] flex flex-col">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2">
          <span className="text-2xl font-bold">
            <span className="text-white">Site</span>
            <span className="text-purple-400">Orbit</span>
          </span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Back to Home */}
          <div className="mb-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center text-gray-300 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </button>
          </div>

          {/* Login Card */}
          <div className="bg-[#1a1a2e] rounded-2xl p-8 shadow-xl border border-purple-500/20">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white">Team Portal</h1>
              <p className="text-gray-400 mt-2">Access your SiteOrbit dashboard</p>
            </div>

            {/* Mobile Auth Error Message */}
            {mobileAuthError && (
              <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-400 text-sm">
                {mobileAuthError}
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Organisation ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 font-mono text-sm">SO-ORG-</span>
                  </div>
                  <input
                    type="text"
                    value={orgIdInput}
                    onChange={handleOrgIdChange}
                    className="w-full pl-24 pr-4 py-3 bg-[#252540] border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors font-mono text-sm"
                    placeholder="XXXX-YY-MM-XXXX"
                    maxLength={17}
                    required
                    data-testid="org-id-input"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Enter your organisation ID (hyphens added automatically)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                  className="w-full px-4 py-3 bg-[#252540] border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                  placeholder="Enter your username"
                  required
                  data-testid="username-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="w-full px-4 py-3 bg-[#252540] border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors pr-12"
                    placeholder="Enter your password"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* DEV: Auto-fill button - only shown in development */}
              {process.env.NODE_ENV === 'development' && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setOrgIdInput('STUD-26-01-PSWL');
                      setLoginData({
                        organisation_id: 'SO-ORG-STUD-26-01-PSWL',
                        username: 'j.phillip',
                        password: '141086Ojay!!'
                      });
                    }}
                    className="flex-1 bg-yellow-600 text-white font-medium py-2 px-4 rounded-xl hover:bg-yellow-700 transition-all text-sm"
                    data-testid="auto-fill-btn"
                  >
                    DEV: Studio G4
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOrgIdInput('G4DE-23-01-LFQO');
                      setLoginData({
                        organisation_id: 'SO-ORG-G4DE-23-01-LFQO',
                        username: 'phillip',
                        password: 'phillip'
                      });
                    }}
                    className="flex-1 bg-yellow-600 text-white font-medium py-2 px-4 rounded-xl hover:bg-yellow-700 transition-all text-sm"
                    data-testid="auto-fill-btn-2"
                  >
                    DEV: G4 Design
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-pink-500/25"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>

              <div className="text-center mt-4">
                <Link
                  to="/forgot-password"
                  className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
          </div>

          {/* Help Text */}
          <p className="text-center text-gray-500 text-sm mt-6">
            Need help? Contact your organisation administrator
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-gray-600 text-sm">
        © {new Date().getFullYear()} SiteOrbit. All rights reserved.
      </footer>
    </div>
  );
};

export default TeamLoginPage;
